/**
 * FirmaVerfuegbarkeit – Inverser Vorlesungskalender
 * ===================================================
 * Zeigt, wann ausgewählte Studierende VERFÜGBAR sind (keine Vorlesung).
 * Wochenansicht mit Studenten-Auswahl und farbcodierter Darstellung.
 */
import { useState, useMemo, useCallback } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '@/api/client'
import type { Teilnehmer } from '@/types'
import Avatar from '@/components/Avatar'
import { ChevronLeft, ChevronRight, Search, X, Check, Users, Clock, CalendarDays } from 'lucide-react'

// ── Helpers ──────────────────────────────────────────────────

const COLORS = [
  { bg: '#ef4444', light: '#fef2f2', border: '#fca5a5', text: '#991b1b' },  // rot
  { bg: '#3b82f6', light: '#eff6ff', border: '#93c5fd', text: '#1e3a8a' },  // blau
  { bg: '#f59e0b', light: '#fffbeb', border: '#fcd34d', text: '#92400e' },  // amber
  { bg: '#8b5cf6', light: '#f5f3ff', border: '#c4b5fd', text: '#5b21b6' },  // violet
  { bg: '#10b981', light: '#ecfdf5', border: '#6ee7b7', text: '#065f46' },  // grün
  { bg: '#ec4899', light: '#fdf2f8', border: '#f9a8d4', text: '#9d174d' },  // pink
  { bg: '#06b6d4', light: '#ecfeff', border: '#67e8f9', text: '#155e75' },  // cyan
  { bg: '#f97316', light: '#fff7ed', border: '#fdba74', text: '#9a3412' },  // orange
]

const DAYS = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa']
const HOURS_START = 8
const HOURS_END = 21
const SLOT_HEIGHT = 48 // px pro Stunde

function getMonday(d: Date): Date {
  const dt = new Date(d)
  const day = dt.getDay()
  const diff = dt.getDate() - day + (day === 0 ? -6 : 1)
  dt.setDate(diff)
  dt.setHours(0, 0, 0, 0)
  return dt
}

function addDays(d: Date, n: number): Date {
  const dt = new Date(d)
  dt.setDate(dt.getDate() + n)
  return dt
}

function formatDateISO(d: Date): string {
  return d.toISOString().split('T')[0]
}

function formatDateShort(d: Date): string {
  return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })
}

function formatWeekLabel(monday: Date): string {
  const sunday = addDays(monday, 6)
  const mStr = monday.toLocaleDateString('de-DE', { day: '2-digit', month: 'long' })
  const sStr = sunday.toLocaleDateString('de-DE', { day: '2-digit', month: 'long', year: 'numeric' })
  return `${mStr} – ${sStr}`
}

function timeToMinutes(t: string | null): number | null {
  if (!t) return null
  const parts = t.split(':')
  if (parts.length < 2) return null
  return parseInt(parts[0]) * 60 + parseInt(parts[1])
}

function isToday(d: Date): boolean {
  const now = new Date()
  return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth() && d.getDate() === now.getDate()
}

// ── Typen ────────────────────────────────────────────────────

interface Props {
  teilnehmer: Teilnehmer[]
  accountId: string
}

interface SelectedStudent {
  stnId: number
  color: typeof COLORS[number]
}

// ── Hauptkomponente ──────────────────────────────────────────

export default function FirmaVerfuegbarkeit({ teilnehmer, accountId }: Props) {
  const [weekOffset, setWeekOffset] = useState(0)
  const [selectedStudents, setSelectedStudents] = useState<SelectedStudent[]>([])
  const [searchTerm, setSearchTerm] = useState('')
  const [showSelector, setShowSelector] = useState(true)

  const monday = useMemo(() => addDays(getMonday(new Date()), weekOffset * 7), [weekOffset])
  const weekDays = useMemo(() => Array.from({ length: 6 }, (_, i) => addDays(monday, i)), [monday])

  // Termine für jeden Tag der Woche laden
  const dateStrings = useMemo(() => weekDays.map(formatDateISO), [weekDays])
  const selectedStnIds = useMemo(() => new Set(selectedStudents.map(s => s.stnId)), [selectedStudents])

  const { data: weekTermine, isLoading } = useQuery({
    queryKey: ['verfuegbarkeit', accountId, dateStrings[0]],
    queryFn: async () => {
      const results = await Promise.all(
        dateStrings.map(d => api.termineHeute(accountId, d))
      )
      return results.map(r => r.termine)
    },
    enabled: selectedStudents.length > 0,
    staleTime: 5 * 60 * 1000,
  })

  // Termine nach Tag und Student gruppieren
  const termineByDayAndStudent = useMemo(() => {
    if (!weekTermine) return []
    return weekTermine.map(dayTermine =>
      dayTermine.filter(t => selectedStnIds.has(t.StudiengangTnId))
    )
  }, [weekTermine, selectedStnIds])

  // Aktive Teilnehmer (ohne Exmatrikulation)
  const aktiveTeilnehmer = useMemo(() =>
    teilnehmer.filter(t => !t.Exmatrikulation),
    [teilnehmer]
  )

  // Suchfilter
  const filteredTeilnehmer = useMemo(() => {
    if (!searchTerm) return aktiveTeilnehmer
    const q = searchTerm.toLowerCase()
    return aktiveTeilnehmer.filter(t =>
      `${t.Vorname} ${t.Nachname}`.toLowerCase().includes(q) ||
      (t.Studiengang ?? '').toLowerCase().includes(q) ||
      (t.Matrikelnr ?? '').includes(q)
    )
  }, [aktiveTeilnehmer, searchTerm])

  const toggleStudent = useCallback((stnId: number) => {
    setSelectedStudents(prev => {
      if (prev.find(s => s.stnId === stnId)) {
        return prev.filter(s => s.stnId !== stnId)
      }
      const usedColors = new Set(prev.map(s => s.color.bg))
      const nextColor = COLORS.find(c => !usedColors.has(c.bg)) ?? COLORS[prev.length % COLORS.length]
      return [...prev, { stnId, color: nextColor }]
    })
  }, [])

  const selectAll = useCallback(() => {
    setSelectedStudents(filteredTeilnehmer.map((t, i) => ({
      stnId: t.StudiengangTnId,
      color: COLORS[i % COLORS.length],
    })))
  }, [filteredTeilnehmer])

  const clearAll = useCallback(() => setSelectedStudents([]), [])

  const getStudentColor = useCallback((stnId: number) => {
    return selectedStudents.find(s => s.stnId === stnId)?.color
  }, [selectedStudents])

  // Verfügbare Zeitfenster berechnen (Lücken zwischen Vorlesungen)
  const getAvailableSlots = useCallback((dayIndex: number) => {
    if (!termineByDayAndStudent[dayIndex]) return []
    const termine = termineByDayAndStudent[dayIndex]
    if (termine.length === 0 && selectedStudents.length > 0) {
      return [{ from: HOURS_START * 60, to: HOURS_END * 60, label: 'Ganztägig verfügbar' }]
    }

    // Alle belegten Zeiten sammeln (über alle ausgewählten Studenten)
    const busy: { from: number; to: number }[] = []
    for (const t of termine) {
      const from = timeToMinutes(t.Von)
      const to = timeToMinutes(t.Bis)
      if (from != null && to != null) busy.push({ from, to })
    }
    busy.sort((a, b) => a.from - b.from)

    // Lücken finden
    const slots: { from: number; to: number; label: string }[] = []
    let cursor = HOURS_START * 60
    for (const b of busy) {
      if (b.from > cursor) {
        const hrs = Math.floor((b.from - cursor) / 60)
        const mins = (b.from - cursor) % 60
        slots.push({ from: cursor, to: b.from, label: `${hrs}h${mins > 0 ? ` ${mins}min` : ''} frei` })
      }
      cursor = Math.max(cursor, b.to)
    }
    if (cursor < HOURS_END * 60) {
      const hrs = Math.floor((HOURS_END * 60 - cursor) / 60)
      const mins = (HOURS_END * 60 - cursor) % 60
      slots.push({ from: cursor, to: HOURS_END * 60, label: `${hrs}h${mins > 0 ? ` ${mins}min` : ''} frei` })
    }
    return slots
  }, [termineByDayAndStudent, selectedStudents.length])

  return (
    <div className="p-6">
      {/* ── Header ── */}
      <div className="flex items-start justify-between mb-6 gap-4">
        <div>
          <h2 className="text-xl font-display font-black text-gray-900">Verfügbarkeitskalender</h2>
          <p className="text-sm text-gray-400 mt-1">
            Sehen Sie, wann Ihre Studierenden keine Vorlesungen haben und für Ihr Unternehmen verfügbar sind.
          </p>
        </div>
        <button
          onClick={() => setShowSelector(!showSelector)}
          className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-all border ${
            showSelector ? 'border-fom-500 bg-fom-50 text-fom-700' : 'border-gray-200 bg-white text-gray-500 hover:bg-gray-50'
          }`}
        >
          <Users size={14} />
          {selectedStudents.length} ausgewählt
        </button>
      </div>

      {/* ── Studenten-Auswahl ── */}
      {showSelector && (
        <div className="bg-white rounded-2xl border border-gray-200 shadow-card mb-6 overflow-hidden">
          <div className="flex items-center gap-3 px-5 py-3 border-b border-gray-100 bg-gray-50/50">
            <Users size={15} className="text-gray-400" />
            <span className="text-sm font-bold text-gray-700">Studierende auswählen</span>
            <span className="text-xs text-gray-400 ml-auto">{selectedStudents.length} / {aktiveTeilnehmer.length}</span>
          </div>

          {/* Suche + Aktionen */}
          <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-100">
            <div className="relative flex-1">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-300" />
              <input
                type="text"
                placeholder="Name, Studiengang oder Matrikelnr. suchen..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-8 py-2 text-sm rounded-lg border border-gray-200 focus:border-fom-400 focus:ring-1 focus:ring-fom-200 outline-none transition-all"
              />
              {searchTerm && (
                <button onClick={() => setSearchTerm('')} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-300 hover:text-gray-500">
                  <X size={14} />
                </button>
              )}
            </div>
            <button onClick={selectAll} className="px-3 py-2 text-xs font-semibold text-fom-600 bg-fom-50 rounded-lg hover:bg-fom-100 transition-colors whitespace-nowrap">
              Alle auswählen
            </button>
            <button onClick={clearAll} className="px-3 py-2 text-xs font-semibold text-gray-500 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors whitespace-nowrap">
              Keine
            </button>
          </div>

          {/* Studenten-Grid */}
          <div className="p-3 max-h-[240px] overflow-y-auto">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {filteredTeilnehmer.map(tn => {
                const color = getStudentColor(tn.StudiengangTnId)
                const selected = !!color
                return (
                  <button
                    key={tn.StudiengangTnId}
                    onClick={() => toggleStudent(tn.StudiengangTnId)}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition-all border ${
                      selected
                        ? 'border-current shadow-sm'
                        : 'border-transparent bg-gray-50 hover:bg-gray-100'
                    }`}
                    style={selected ? { borderColor: color.bg, backgroundColor: color.light, color: color.text } : undefined}
                  >
                    {/* Avatar (Foto mit Initial-Fallback) + Check bei Auswahl */}
                    {selected ? (
                      <div
                        className="w-8 h-8 rounded-full flex items-center justify-center shrink-0"
                        style={{ backgroundColor: color.bg, color: '#fff' }}
                      >
                        <Check size={14} />
                      </div>
                    ) : (
                      <Avatar
                        photoUrl={`/api/firma/${accountId}/teilnehmer/${tn.PersonenFID}/photo`}
                        fallbackName={`${tn.Vorname ?? ''} ${tn.Nachname ?? ''}`.trim()}
                        size={32}
                      />
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold truncate" style={selected ? { color: color.text } : { color: '#1e293b' }}>
                        {tn.Vorname} {tn.Nachname}
                      </div>
                      <div className="text-[11px] truncate" style={{ color: selected ? color.text : '#94a3b8', opacity: selected ? 0.7 : 1 }}>
                        {tn.Studiengang ?? tn.Kuerzel ?? '–'}
                      </div>
                    </div>
                  </button>
                )
              })}
            </div>
            {filteredTeilnehmer.length === 0 && (
              <div className="text-center text-gray-400 text-sm py-8">Keine Studierenden gefunden.</div>
            )}
          </div>
        </div>
      )}

      {/* ── Ausgewählte Studenten Legende ── */}
      {selectedStudents.length > 0 && !showSelector && (
        <div className="flex flex-wrap gap-2 mb-4">
          {selectedStudents.map(s => {
            const tn = teilnehmer.find(t => t.StudiengangTnId === s.stnId)
            if (!tn) return null
            return (
              <span
                key={s.stnId}
                className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold cursor-pointer hover:opacity-80 transition-opacity"
                style={{ backgroundColor: s.color.light, color: s.color.text, border: `1px solid ${s.color.border}` }}
                onClick={() => toggleStudent(s.stnId)}
              >
                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: s.color.bg }} />
                {tn.Vorname} {tn.Nachname}
                <X size={11} className="ml-0.5" />
              </span>
            )
          })}
        </div>
      )}

      {/* ── Keine Auswahl ── */}
      {selectedStudents.length === 0 && (
        <div className="bg-white rounded-2xl border border-gray-200 shadow-card p-12 text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-fom-50 flex items-center justify-center">
            <CalendarDays size={28} className="text-fom-400" />
          </div>
          <h3 className="text-lg font-display font-black text-gray-900">Studierende auswählen</h3>
          <p className="text-sm text-gray-400 mt-2 max-w-md mx-auto">
            Wählen Sie oben die Studierenden aus, deren Verfügbarkeit Sie vergleichen möchten.
            Der Kalender zeigt Ihnen dann, wann diese für Ihr Unternehmen verfügbar sind.
          </p>
        </div>
      )}

      {/* ── Kalender ── */}
      {selectedStudents.length > 0 && (
        <div className="bg-white rounded-2xl border border-gray-200 shadow-card overflow-hidden">
          {/* Wochen-Navigation */}
          <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100 bg-gray-50/50">
            <button onClick={() => setWeekOffset(w => w - 1)} className="p-1.5 rounded-lg hover:bg-gray-200 transition-colors text-gray-500">
              <ChevronLeft size={18} />
            </button>
            <div className="text-center">
              <div className="text-sm font-bold text-gray-900">{formatWeekLabel(monday)}</div>
              {weekOffset !== 0 && (
                <button onClick={() => setWeekOffset(0)} className="text-[11px] text-fom-500 font-semibold hover:text-fom-700 mt-0.5">
                  Zur aktuellen Woche
                </button>
              )}
            </div>
            <button onClick={() => setWeekOffset(w => w + 1)} className="p-1.5 rounded-lg hover:bg-gray-200 transition-colors text-gray-500">
              <ChevronRight size={18} />
            </button>
          </div>

          {/* Loading */}
          {isLoading && (
            <div className="flex items-center justify-center py-16">
              <div className="text-sm text-gray-400 animate-pulse">Termine werden geladen...</div>
            </div>
          )}

          {/* Kalender-Grid */}
          {!isLoading && (
            <div className="flex">
              {/* Zeitachse */}
              <div className="shrink-0 w-14 border-r border-gray-100 pt-10">
                {Array.from({ length: HOURS_END - HOURS_START }, (_, i) => (
                  <div key={i} className="relative" style={{ height: SLOT_HEIGHT }}>
                    <span className="absolute -top-2 right-2 text-[10px] font-semibold text-gray-400 tabular-nums">
                      {(HOURS_START + i).toString().padStart(2, '0')}:00
                    </span>
                  </div>
                ))}
              </div>

              {/* Tages-Spalten */}
              <div className="flex-1 grid" style={{ gridTemplateColumns: `repeat(${weekDays.length}, 1fr)` }}>
                {weekDays.map((day, dayIdx) => {
                  const dayTermine = termineByDayAndStudent[dayIdx] ?? []
                  const availSlots = getAvailableSlots(dayIdx)
                  const today = isToday(day)
                  const isPast = day < new Date(new Date().setHours(0, 0, 0, 0))

                  return (
                    <div key={dayIdx} className={`border-r border-gray-100 last:border-r-0 ${today ? 'bg-fom-50/30' : ''}`}>
                      {/* Tag-Header */}
                      <div className={`text-center py-2.5 border-b border-gray-100 sticky top-0 z-10 ${today ? 'bg-fom-50' : 'bg-white'}`}>
                        <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">{DAYS[dayIdx]}</div>
                        <div className={`text-sm font-black ${today ? 'text-fom-600' : 'text-gray-900'}`}>
                          {formatDateShort(day)}
                        </div>
                      </div>

                      {/* Zeitslots */}
                      <div className="relative" style={{ height: (HOURS_END - HOURS_START) * SLOT_HEIGHT }}>
                        {/* Stunden-Raster */}
                        {Array.from({ length: HOURS_END - HOURS_START }, (_, i) => (
                          <div key={i} className="absolute w-full border-b border-gray-50" style={{ top: i * SLOT_HEIGHT, height: SLOT_HEIGHT }} />
                        ))}

                        {/* Verfügbare Zeitfenster (grün) */}
                        {availSlots.map((slot, i) => {
                          const top = ((slot.from - HOURS_START * 60) / 60) * SLOT_HEIGHT
                          const height = ((slot.to - slot.from) / 60) * SLOT_HEIGHT
                          if (height < 4) return null
                          return (
                            <div
                              key={`avail-${i}`}
                              className="absolute left-0.5 right-0.5 rounded-md border border-emerald-200 bg-emerald-50/60 flex items-center justify-center overflow-hidden transition-all hover:bg-emerald-100/80"
                              style={{ top, height, zIndex: 1 }}
                            >
                              {height > 20 && (
                                <div className="text-[10px] font-semibold text-emerald-600 text-center px-1 leading-tight">
                                  <Clock size={10} className="inline mr-0.5 -mt-0.5" />
                                  {slot.label}
                                </div>
                              )}
                            </div>
                          )
                        })}

                        {/* Vorlesungsblöcke (pro Student) */}
                        {dayTermine.map((t, tIdx) => {
                          const from = timeToMinutes(t.Von)
                          const to = timeToMinutes(t.Bis)
                          if (from == null || to == null) return null
                          const color = getStudentColor(t.StudiengangTnId)
                          if (!color) return null

                          const top = ((from - HOURS_START * 60) / 60) * SLOT_HEIGHT
                          const height = Math.max(((to - from) / 60) * SLOT_HEIGHT, 16)

                          // Mehrere Studenten am gleichen Zeitslot nebeneinander
                          const sameSlot = dayTermine.filter(o =>
                            o !== t &&
                            timeToMinutes(o.Von) != null &&
                            timeToMinutes(o.Bis) != null &&
                            timeToMinutes(o.Von)! < to &&
                            timeToMinutes(o.Bis)! > from
                          )
                          const totalInSlot = sameSlot.length + 1
                          const myIndex = sameSlot.filter(o => {
                            const idx1 = selectedStudents.findIndex(s => s.stnId === t.StudiengangTnId)
                            const idx2 = selectedStudents.findIndex(s => s.stnId === o.StudiengangTnId)
                            return idx2 < idx1
                          }).length
                          const widthPct = 100 / totalInSlot
                          const leftPct = myIndex * widthPct

                          return (
                            <div
                              key={`${t.BuchungsID}-${tIdx}`}
                              className="absolute rounded-md border overflow-hidden transition-all hover:z-20 hover:shadow-md group"
                              style={{
                                top,
                                height,
                                left: `calc(${leftPct}% + 2px)`,
                                width: `calc(${widthPct}% - 4px)`,
                                backgroundColor: color.light,
                                borderColor: color.border,
                                opacity: isPast ? 0.5 : (t.Stattgefunden === 'N' ? 0.4 : 1),
                                zIndex: 5,
                              }}
                            >
                              <div
                                className="absolute top-0 left-0 w-1 h-full rounded-l-md"
                                style={{ backgroundColor: color.bg }}
                              />
                              <div className="pl-2.5 pr-1 py-0.5 h-full flex flex-col justify-center overflow-hidden">
                                <div className="text-[10px] font-bold truncate" style={{ color: color.text }}>
                                  {t.Fach ?? '–'}
                                </div>
                                {height > 32 && (
                                  <div className="text-[9px] truncate" style={{ color: color.text, opacity: 0.7 }}>
                                    {t.Vorname} {t.Nachname}
                                  </div>
                                )}
                                {height > 48 && t.Von && t.Bis && (
                                  <div className="text-[9px] tabular-nums mt-0.5" style={{ color: color.text, opacity: 0.5 }}>
                                    {t.Von?.slice(0, 5)}–{t.Bis?.slice(0, 5)}
                                  </div>
                                )}
                              </div>
                              {/* Tooltip */}
                              <div className="hidden group-hover:block absolute bottom-full left-1/2 -translate-x-1/2 mb-1 z-50">
                                <div className="bg-gray-900 text-white rounded-lg px-3 py-2 text-xs shadow-lg whitespace-nowrap">
                                  <div className="font-bold">{t.Vorname} {t.Nachname}</div>
                                  <div className="text-gray-300">{t.Fach}</div>
                                  <div className="text-gray-400">{t.Von?.slice(0, 5)}–{t.Bis?.slice(0, 5)} · {t.Dozent ?? ''}</div>
                                  {t.Stattgefunden === 'N' && <div className="text-red-400 font-semibold mt-0.5">Ausgefallen</div>}
                                </div>
                              </div>
                            </div>
                          )
                        })}

                        {/* Jetzt-Linie */}
                        {today && (() => {
                          const now = new Date()
                          const mins = now.getHours() * 60 + now.getMinutes()
                          if (mins < HOURS_START * 60 || mins > HOURS_END * 60) return null
                          const top = ((mins - HOURS_START * 60) / 60) * SLOT_HEIGHT
                          return (
                            <div className="absolute left-0 right-0 z-30 pointer-events-none" style={{ top }}>
                              <div className="flex items-center">
                                <div className="w-2 h-2 rounded-full bg-red-500 -ml-1 shrink-0" />
                                <div className="flex-1 h-[2px] bg-red-500" />
                              </div>
                            </div>
                          )
                        })()}
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          )}

          {/* Legende */}
          <div className="flex items-center gap-6 px-5 py-3 border-t border-gray-100 bg-gray-50/50">
            <div className="flex items-center gap-2 text-xs text-gray-500">
              <div className="w-4 h-3 rounded-sm bg-emerald-100 border border-emerald-200" />
              Verfügbar
            </div>
            {selectedStudents.map(s => {
              const tn = teilnehmer.find(t => t.StudiengangTnId === s.stnId)
              return (
                <div key={s.stnId} className="flex items-center gap-2 text-xs">
                  <div className="w-4 h-3 rounded-sm border" style={{ backgroundColor: s.color.light, borderColor: s.color.border }} />
                  <span style={{ color: s.color.text, fontWeight: 600 }}>
                    {tn ? `${tn.Vorname} ${tn.Nachname}` : '–'}
                  </span>
                </div>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}
